
import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Classes from "./Membership.module.css";
import {
  EmailSentIcon,
  ExclamationIcon,
} from "../../../../../../../../utils/Icons";
import NotifyPopup from "../../../../../../../GlobalComponents/NotifyPopup/NotifyPopup";
import backend from "../../../../../../../../api";
import { useSelector } from "react-redux";

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: "easeOut" },
  },
};

const cardIn = {
  hidden: { opacity: 0, y: 10, scale: 0.99 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.25 },
  },
};

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");
const safeNum = (v, fallback = 0) => {
  if (v === "" || v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const titleForKey = (key) => {
  if (key === "tier1") return "Tier 1";
  if (key === "tier2") return "Tier 2";
  if (key === "tier3") return "Tier 3";
  return "Fleet";
};

export default function AdminSubscriptionControl() {
  const { user, token } = useSelector((s) => s?.auth || {});
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

  const companyId = user?.companyId;
  const adminUserId = user?._id;

  // ✅ Frontend guard (still secure your API with middleware)
  const isAdmin =
    user?.role === "company" || user?.role === "admin" || user?.isAdmin === true;

  // Tabs
  const [tab, setTab] = useState("pricing"); // pricing | fleet

  // Toast popup
  const [toast, setToast] = useState({
    open: false,
    title: "",
    description: "",
    icon: null,
  });

  const openToast = (
    title,
    description,
    icon = <EmailSentIcon fill={"white"} />
  ) => setToast({ open: true, title, description, icon });

  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  // -------------------------
  // PLAN CONFIG (Admin)
  // -------------------------
  const [planItems, setPlanItems] = useState(null); // null loading
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [savingKey, setSavingKey] = useState("");

  // local editable state (ONLY price + vehicles)
  const [formByKey, setFormByKey] = useState({});

  const loadPlans = async () => {
    setLoadingPlans(true);
    try {
      const { data } = await backend.get("/api/plan-config/admin/all", {
        headers,
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      const order = ["tier1", "tier2", "tier3", "fleet"];
      items.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
      setPlanItems(items);

      const next = {};
      for (const p of items) {
        next[p.key] = {
          vehiclesLimit: safeNum(p.vehiclesLimit, 0),
          monthlyAmount: safeNum(p?.pricing?.monthly?.amount, 0),
          biannuallyAmount: safeNum(p?.pricing?.biannually?.amount, 0),
          annuallyAmount: safeNum(p?.pricing?.annually?.amount, 0),
          perVehiclePrice: safeNum(p?.perVehiclePrice, 0),
        };
      }
      setFormByKey(next);
    } catch (e) {
      console.error("load plans error:", e);
      setPlanItems([]);
      openToast(
        "Failed to load pricing",
        e?.response?.data?.error || e?.message || "Unknown error",
        <ExclamationIcon fill={"white"} height="50px" />
      );
    } finally {
      setLoadingPlans(false);
    }
  };

  const savePlan = async (key) => {
    // 🔒 Tier1 must never be saved/edited from UI
    if (key === "tier1") {
      openToast(
        "Tier 1 is locked",
        "Tier 1 is a free tier and cannot be modified.",
        <ExclamationIcon fill={"white"} height="50px" />
      );
      return;
    }

    const f = formByKey[key];
    if (!f) return;

    setSavingKey(key);
    try {
      const isFleet = key === "fleet";

      // ✅ ONLY send allowed fields
      const patch = isFleet
        ? { perVehiclePrice: safeNum(f.perVehiclePrice, 0) }
        : {
            vehiclesLimit: safeNum(f.vehiclesLimit, 0),
            pricing: {
              monthly: { amount: safeNum(f.monthlyAmount, 0) },
              biannually: { amount: safeNum(f.biannuallyAmount, 0) },
              annually: { amount: safeNum(f.annuallyAmount, 0) },
            },
          };

      const { data } = await backend.put(`/api/plan-config/admin/${key}`, patch, {
        headers,
      });
      const updated = data?.item;

      setPlanItems((prev) => {
        const arr = Array.isArray(prev) ? [...prev] : [];
        const idx = arr.findIndex((x) => x.key === key);
        if (idx >= 0) arr[idx] = updated;
        return arr;
      });

      openToast("Saved", `${key.toUpperCase()} updated successfully.`);
    } catch (e) {
      console.error("save plan error:", e);
      openToast(
        "Save failed",
        e?.response?.data?.error || e?.message || "Unknown error",
        <ExclamationIcon fill={"white"} height="50px" />
      );
    } finally {
      setSavingKey("");
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAdmin]);

  const orderedPlans = useMemo(() => {
    const items = Array.isArray(planItems) ? planItems : [];
    const order = ["tier1", "tier2", "tier3", "fleet"];
    return [...items].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }, [planItems]);

  // -------------------------
  // FLEET REQUESTS (Admin)
  // -------------------------
  const [fleetStatus, setFleetStatus] = useState("pending");
  const [fleetItems, setFleetItems] = useState(null);
  const [fleetLoading, setFleetLoading] = useState(false);
  const [decidingId, setDecidingId] = useState("");

  const [decisionModal, setDecisionModal] = useState({
    open: false,
    request: null,
    decision: "approved", // approved | rejected
    pricePerVehicle: "",
  });

  const loadFleet = async () => {
    setFleetLoading(true);
    try {
      const { data } = await backend.get("/api/fleet-requests/admin/list", {
        headers,
        params: { companyId, status: fleetStatus },
      });
      setFleetItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      console.error("load fleet error:", e);
      setFleetItems([]);
      openToast(
        "Failed to load fleet requests",
        e?.response?.data?.error || e?.message || "Unknown error",
        <ExclamationIcon fill={"white"} height="50px" />
      );
    } finally {
      setFleetLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    if (tab !== "fleet") return;
    loadFleet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, fleetStatus, token, isAdmin]);

  const openDecision = (reqDoc, decision) => {
    setDecisionModal({
      open: true,
      request: reqDoc,
      decision,
      pricePerVehicle:
        decision === "approved"
          ? String(reqDoc?.approvedPricePerVehicle ?? "")
          : "",
    });
  };

  const closeDecision = () =>
    setDecisionModal({
      open: false,
      request: null,
      decision: "approved",
      pricePerVehicle: "",
    });

  const submitDecision = async () => {
    const r = decisionModal.request;
    if (!r?._id) return;

    if (decisionModal.decision === "approved") {
      if (decisionModal.pricePerVehicle === "") {
        openToast(
          "Missing price",
          "Please enter Price Per Vehicle to approve.",
          <ExclamationIcon fill={"white"} height="50px" />
        );
        return;
      }
    }

    setDecidingId(r._id);
    try {
      // ✅ ONLY allow admin to set pricePerVehicle
      await backend.post(
        "/api/fleet-requests/admin/decision",
        {
          requestId: r._id,
          decision: decisionModal.decision,
          adminUserId,
          pricePerVehicle:
            decisionModal.decision === "approved"
              ? safeNum(decisionModal.pricePerVehicle, 0)
              : undefined,
        },
        { headers }
      );

      openToast(
        "Updated",
        decisionModal.decision === "approved"
          ? "Fleet request approved."
          : "Fleet request rejected."
      );

      closeDecision();
      await loadFleet();
    } catch (e) {
      console.error("fleet decision error:", e);
      openToast(
        "Decision failed",
        e?.response?.data?.error || e?.message || "Unknown error",
        <ExclamationIcon fill={"white"} height="50px" />
      );
    } finally {
      setDecidingId("");
    }
  };

  // -------------------------
  // UI
  // -------------------------
  if (!isAdmin) {
    return (
      <>
        <h3 className="mb-7">Subscriptions Control</h3>
        <div className={Classes.adminGuard}>
          You don’t have permission to access this page.
        </div>
      </>
    );
  }

  return (
    <>
      {/* Tabs */}
      <motion.div
        className={Classes.adminTabsWrap}
        initial="hidden"
        animate="show"
        variants={fadeUp}
      >
        <div className={Classes.adminTabs}>
          <motion.span
            className={Classes.adminTabsBg}
            animate={{ left: tab === "pricing" ? "4px" : "calc(50% + 4px)" }}
            transition={{ type: "spring", stiffness: 520, damping: 32 }}
          />
          <button
            type="button"
            className={`${Classes.adminTabBtn} ${
              tab === "pricing" ? Classes.active : ""
            }`}
            onClick={() => setTab("pricing")}
          >
            Pricing
          </button>
          <button
            type="button"
            className={`${Classes.adminTabBtn} ${
              tab === "fleet" ? Classes.active : ""
            }`}
            onClick={() => setTab("fleet")}
          >
            Fleet Requests
          </button>
        </div>
      </motion.div>

      {/* =======================
          TAB: PRICING
         ======================= */}
      {tab === "pricing" && (
        <motion.section
          className={Classes.page}
          initial="hidden"
          animate="show"
          variants={fadeUp}
        >
          <div className={Classes.adminHero}>
            <div>
              <div className={Classes.adminHeroTitle}>Plan Pricing</div>
              <div className={Classes.adminHeroSub}>
                Changes reflect instantly for customers on Membership page.
              </div>
            </div>
          </div>

          <div className={Classes.adminCards}>
            {planItems === null ? (
              <>
                {[1, 2, 3].map((i) => (
                  <motion.div
                    key={`sk_${i}`}
                    className={Classes.adminCard}
                    variants={cardIn}
                    style={{ opacity: 0.7 }}
                  >
                    <div className={Classes.adminCardTop}>
                      <div className={Classes.adminCardTitle}>Loading...</div>
                      <div className={Classes.adminPill}>Please wait</div>
                    </div>

                    <div className={Classes.adminGrid}>
                      <div className={Classes.adminField}>
                        <div className={Classes.adminLabel}>Monthly</div>
                        <div className={Classes.adminFakeInput} />
                      </div>
                      <div className={Classes.adminField}>
                        <div className={Classes.adminLabel}>Bi-Annually</div>
                        <div className={Classes.adminFakeInput} />
                      </div>
                      <div className={Classes.adminField}>
                        <div className={Classes.adminLabel}>Annually</div>
                        <div className={Classes.adminFakeInput} />
                      </div>
                      <div className={Classes.adminField}>
                        <div className={Classes.adminLabel}>Vehicles</div>
                        <div className={Classes.adminFakeInput} />
                      </div>
                    </div>

                    <button className={`${Classes.cta} ${Classes.disabledBtn}`} disabled>
                      Loading...
                    </button>
                  </motion.div>
                ))}
              </>
            ) : (
              orderedPlans.map((p) => {
                const key = p.key;
                const isFleet = key === "fleet";
                const isTier1 = key === "tier1";
                const f = formByKey[key] || {};
                const saving = savingKey === key;

                return (
                  <motion.div
                    key={key}
                    className={Classes.adminCard}
                    variants={cardIn}
                  >
                    <div className={Classes.adminCardTop}>
                      <div className={Classes.adminCardTitle}>
                        {titleForKey(key)}
                      </div>
                      <div className={Classes.adminPill}>
                        {isTier1
                          ? "Free Tier (Locked)"
                          : isFleet
                          ? "Per Vehicle Pricing"
                          : "Tier Pricing"}
                      </div>
                    </div>

                    {/* ✅ Tier 1: show ONLY info block, NO inputs */}
                    {isTier1 ? (
                      <div className={Classes.adminFreeTierBox}>
                        <div className={Classes.adminFreeTierTitle}>
                          Tier 1 is Free
                        </div>
                        <div className={Classes.adminFreeTierText}>
                          This tier is <strong>free forever</strong>
                        </div>
                       
                      </div>
                    ) : !isFleet ? (
                      <div className={Classes.adminGrid}>
                        <div className={Classes.adminField}>
                          <label className={Classes.adminLabel}>
                            Monthly ($)
                          </label>
                          <input
                            className={Classes.adminInput}
                            type="number"
                            value={f.monthlyAmount ?? 0}
                            onChange={(e) =>
                              setFormByKey((s) => ({
                                ...s,
                                [key]: {
                                  ...s[key],
                                  monthlyAmount: e.target.value,
                                },
                              }))
                            }
                          />
                        </div>

                        <div className={Classes.adminField}>
                          <label className={Classes.adminLabel}>
                            Bi-Annually ($)
                          </label>
                          <input
                            className={Classes.adminInput}
                            type="number"
                            value={f.biannuallyAmount ?? 0}
                            onChange={(e) =>
                              setFormByKey((s) => ({
                                ...s,
                                [key]: {
                                  ...s[key],
                                  biannuallyAmount: e.target.value,
                                },
                              }))
                            }
                          />
                        </div>

                        <div className={Classes.adminField}>
                          <label className={Classes.adminLabel}>
                            Annually ($)
                          </label>
                          <input
                            className={Classes.adminInput}
                            type="number"
                            value={f.annuallyAmount ?? 0}
                            onChange={(e) =>
                              setFormByKey((s) => ({
                                ...s,
                                [key]: {
                                  ...s[key],
                                  annuallyAmount: e.target.value,
                                },
                              }))
                            }
                          />
                        </div>

                        <div className={Classes.adminField}>
                          <label className={Classes.adminLabel}>
                            Vehicles Limit
                          </label>
                          <input
                            className={Classes.adminInput}
                            type="number"
                            value={f.vehiclesLimit ?? 0}
                            onChange={(e) =>
                              setFormByKey((s) => ({
                                ...s,
                                [key]: {
                                  ...s[key],
                                  vehiclesLimit: e.target.value,
                                },
                              }))
                            }
                          />
                          <div className={Classes.adminHintText}>
                            Use <strong>0</strong> for unlimited.
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className={Classes.adminGridFleet}>
                        <div className={Classes.adminField}>
                          <label className={Classes.adminLabel}>
                            Price Per Vehicle / Month ($)
                          </label>
                          <input
                            className={Classes.adminInput}
                            type="number"
                            value={f.perVehiclePrice ?? 0}
                            onChange={(e) =>
                              setFormByKey((s) => ({
                                ...s,
                                [key]: {
                                  ...s[key],
                                  perVehiclePrice: e.target.value,
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                    )}

                    {/* ✅ Save button hidden for Tier 1 */}
                    {!isTier1 && (
                      <button
                        type="button"
                        className={`${Classes.cta} ${
                          saving ? Classes.disabledBtn : ""
                        }`}
                        onClick={saving ? undefined : () => savePlan(key)}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>
        </motion.section>
      )}

      {/* =======================
          TAB: FLEET REQUESTS
         ======================= */}
      {tab === "fleet" && (
        <motion.section
          className={Classes.page}
          initial="hidden"
          animate="show"
          variants={fadeUp}
        >
          <div className={Classes.adminHero}>
            <div>
              <div className={Classes.adminHeroTitle}>Fleet Requests</div>
              <div className={Classes.adminHeroSub}>
                Approve/Reject requests. Approval requires only price per vehicle.
              </div>
            </div>

            <div className={Classes.adminFleetFilters}>
              <select
                className={Classes.adminSelect}
                value={fleetStatus}
                onChange={(e) => setFleetStatus(e.target.value)}
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          <div className={Classes.adminList}>
            {fleetItems === null ? (
              <div className={Classes.adminEmpty}>Loading fleet requests...</div>
            ) : fleetItems.length === 0 ? (
              <div className={Classes.adminEmpty}>No fleet requests found.</div>
            ) : (
              fleetItems.map((r) => (
                <div key={r._id} className={Classes.adminItem}>
                  <div className={Classes.adminItemLeft}>
                    <div className={Classes.adminItemTitle}>
                      Fleet Request{" "}
                      <span
                        className={`${Classes.adminStatusPill} ${
                          r.status === "approved"
                            ? Classes.statusApproved
                            : r.status === "rejected"
                            ? Classes.statusRejected
                            : Classes.statusPending
                        }`}
                      >
                        {r.status}
                      </span>
                    </div>

                    <div className={Classes.adminItemMeta}>
                      <span>
                        <strong>User:</strong> {String(r.userId?.email)}
                      </span>
                      <span>
                        <strong>Vehicles:</strong> {r.vehiclesCount}
                      </span>
                      <span>
                        <strong>Created:</strong> {fmt(r.createdAt)}
                      </span>
                    </div>

                    {r.notes ? (
                      <div className={Classes.adminItemNotes}>
                        <strong>Notes:</strong> {r.notes}
                      </div>
                    ) : null}

                    {r.status === "approved" ? (
                      <div
                        className={Classes.adminItemMeta}
                        style={{ marginTop: 8 }}
                      >
                        <span>
                          <strong>Approved Price/Vehicle:</strong>{" "}
                          {r.approvedPricePerVehicle ?? "—"}
                        </span>
                        <span>
                          <strong>Approved At:</strong> {fmt(r.approvedAt)}
                        </span>
                      </div>
                    ) : null}
                  </div>

                  <div className={Classes.adminItemRight}>
                    <button
                      className={`${Classes.cta} ${
                        decidingId === r._id ? Classes.disabledBtn : ""
                      }`}
                      disabled={decidingId === r._id}
                      onClick={() => openDecision(r, "approved")}
                      style={{ width: 170 }}
                    >
                      Approve
                    </button>

                    <button
                      className={`${Classes.cta} ${Classes.ghostBtn} ${
                        decidingId === r._id ? Classes.disabledBtn : ""
                      }`}
                      disabled={decidingId === r._id}
                      onClick={() => openDecision(r, "rejected")}
                      style={{ width: 170 }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.section>
      )}

      {/* Decision Modal (price per vehicle only on approval) */}
      <AnimatePresence>
        {decisionModal.open && (
          <motion.div
            className={Classes.adminModalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={Classes.adminModal}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ duration: 0.18 }}
            >
              <div className={Classes.adminModalHead}>
                <div className={Classes.adminModalTitle}>
                  {decisionModal.decision === "approved"
                    ? "Approve Fleet Request"
                    : "Reject Fleet Request"}
                </div>
                <button
                  type="button"
                  className={Classes.adminModalClose}
                  onClick={closeDecision}
                >
                  ✕
                </button>
              </div>

              <div className={Classes.adminModalBody}>
                <div className={Classes.adminItemMeta}>
                  <span>
                    <strong>Vehicles:</strong>{" "}
                    {decisionModal.request?.vehiclesCount}
                  </span>
                  <span>
                    <strong>User:</strong>{" "}
                    {String(decisionModal.request?.userId)}
                  </span>
                  <span>
                    <strong>Created:</strong>{" "}
                    {fmt(decisionModal.request?.createdAt)}
                  </span>
                </div>

                {decisionModal.decision === "approved" ? (
                  <div className={Classes.adminField}>
                    <label className={Classes.adminLabel}>
                      Price Per Vehicle ($)
                    </label>
                    <input
                      className={Classes.adminInput}
                      type="number"
                      value={decisionModal.pricePerVehicle}
                      onChange={(e) =>
                        setDecisionModal((s) => ({
                          ...s,
                          pricePerVehicle: e.target.value,
                        }))
                      }
                      placeholder="e.g. 25"
                    />
                    <div className={Classes.adminHintText}>
                      Vehicles count is read-only (customer requested{" "}
                      <strong>{decisionModal.request?.vehiclesCount}</strong>).
                    </div>
                  </div>
                ) : (
                  <div className={Classes.adminRejectBox}>
                    This will mark the request as <strong>rejected</strong> and
                    notify the customer.
                  </div>
                )}
              </div>

              <div className={Classes.adminModalActions}>
                <button
                  type="button"
                  className={`${Classes.cta} ${Classes.ghostBtn}`}
                  onClick={closeDecision}
                  disabled={!!decidingId}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`${Classes.cta} ${
                    decidingId ? Classes.disabledBtn : ""
                  }`}
                  onClick={submitDecision}
                  disabled={!!decidingId}
                >
                  {decidingId ? "Processing..." : "Confirm"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence exit="wait">
        {toast.open && (
          <NotifyPopup
            closePopup={closeToast}
            submitPopup={closeToast}
            title={toast.title}
            description={toast.description}
            buttonText="OK"
            icon={toast.icon || <EmailSentIcon fill={"white"} />}
          />
        )}
      </AnimatePresence>
    </>
  );
}
